import { createElement, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cx } from './library-kit';

/** The type roles of the Claude Design (tokens/typography.css): Hanken Grotesk for all of them; mono only for data. */
export type TextRole = 'hero' | 'display' | 'headline' | 'title' | 'body-lg' | 'body' | 'label' | 'caption' | 'micro';
/** The colour of a text: the text tokens plus the three status inks. */
export type TextTone = 'primary' | 'secondary' | 'tertiary' | 'disabled' | 'inverse' | 'danger' | 'success' | 'warning';

type TextTag = 'span' | 'p' | 'div' | 'label' | 'strong' | 'em' | 'small' | 'code' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'dt' | 'dd' | 'legend' | 'figcaption';

export interface TextProps extends Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children' | 'role'> {
  /** The type role: size, line height, weight and tracking all come from it. Default `body`. */
  readonly role?: TextRole;
  /** The ink. Default `primary`. */
  readonly tone?: TextTone;
  /** Data (an id, a date, a count, a code, a price): IBM Plex Mono, tabular, weight 500. Text and headings are never mono. */
  readonly mono?: boolean;
  /** The element. Default `span` (inline); use `p`/`h2`… for the right semantics. */
  readonly as?: TextTag;
  /** Render as a block (a `span` that fills its line). Elements that are blocks anyway ignore it. */
  readonly block?: boolean;
  /** One line with an ellipsis. */
  readonly truncate?: boolean;
  /** `htmlFor`, when `as="label"`. */
  readonly htmlFor?: string;
  readonly className?: string;
  readonly children?: ReactNode;
}

/**
 * Text by role (D548): the replacement for the `CAPTION` / `fontSize` / `fontWeight` / hand-styled-heading inline styles of
 * the screens. `<Text role="caption" tone="tertiary">…</Text>` is the hint line; `<Text role="title" as="h2">…</Text>` a
 * section heading; `<Text mono>R$ 12,00</Text>` a data value. No size, weight or colour can be passed in — only a role and a tone.
 */
export function Text({ role = 'body', tone = 'primary', mono = false, as = 'span', block = false, truncate = false, className, children, ...props }: TextProps): ReactElement {
  // `style` is not in the prop types; a loosely-typed caller that still passes one gets it dropped, not applied.
  const { style: _style, ...rest } = props as typeof props & { style?: unknown };
  return createElement(
    as,
    {
      ...rest,
      className: cx('vello-text', className),
      'data-role': role,
      'data-tone': tone === 'primary' ? undefined : tone,
      'data-mono': mono ? 'true' : undefined,
      'data-block': block ? 'true' : undefined,
      'data-truncate': truncate ? 'true' : undefined,
    },
    children,
  );
}
